import { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { HeyatYoluContext } from "../../App";
import axios from "../../services/api";
import { toast } from "react-toastify";
import "./Navbar.css";

function Navbar() {
  const { user, setUser } = useContext(HeyatYoluContext);
  const navigate = useNavigate();
  const location = useLocation();

  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const [visibleCount, setVisibleCount] = useState(5);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  const allLinks = [
    { path: "/sehidlerimiz", label: "🇦🇿 Şəhidlərimiz" },
    { path: "/il-donumu", label: "📅 İl Dönümü" },
    { path: "/layihe-haqqinda", label: "Layihə Haqqında" },
    { path: "/elaqe", label: "Əlaqə" },
  ];

  const bottomNavItems = [
    { path: "/", icon: "fas fa-home", label: "Ana səhifə" },
    { path: "/sehidlerimiz", icon: "fas fa-star-and-crescent", label: "Şəhidlər" },
    { path: "/xatire-yarat", icon: "fas fa-plus", label: "Yarat", isCreate: true },
    { path: "/il-donumu", icon: "fas fa-calendar-alt", label: "İl Dönümü" },
    {
      path: user ? "/kabinetim" : "/giris",
      icon: user ? "fas fa-user" : "fas fa-sign-in-alt",
      label: user ? "Kabinet" : "Giriş"
    },
  ];

  const secondaryLinks = [
    { path: "/layihe-haqqinda", label: "ℹ️ Layihə Haqqında" },
    { path: "/elaqe", label: "📩 Əlaqə" },
  ];

  const handleLogout = async () => {
    await axios.post("/account/logout", {}, { withCredentials: true });
    setUser(null);
    navigate("/giris");
    setShowMobileMenu(false);
  };

  const handleMemoryCreate = () => {
    if (!user) {
      toast.info("Zəhmət olmasa, xatirə yaratmaq üçün daxil olun..");
      navigate("/giris", { state: { redirectTo: "/xatire-yarat" } });
    } else {
      navigate("/xatire-yarat");
    }
    setShowMobileMenu(false);
  };

  const handleBottomNavClick = (item) => {
    if (item.isCreate) {
      handleMemoryCreate();
    } else {
      navigate(item.path);
    }
  };

  const isActiveRoute = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
      updateVisibleCount();
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const updateVisibleCount = () => {
    if (!containerRef.current || window.innerWidth <= 992) {
      setVisibleCount(0);
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const logoWidth = 180;
    const rightSideWidth = user ? 340 : 280;
    const linkWidth = 170;
    const dropdownWidth = 140;
    const padding = 40;

    const availableWidth = containerWidth - logoWidth - rightSideWidth - padding;
    let count = Math.floor(availableWidth / linkWidth);

    if (count < allLinks.length) {
      const withDropdown = containerWidth - logoWidth - rightSideWidth - dropdownWidth - padding;
      count = Math.floor(withDropdown / linkWidth);
    }

    setVisibleCount(Math.max(0, Math.min(count, allLinks.length)));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visibleLinks = isMobile ? [] : allLinks.slice(0, visibleCount);
  const hiddenLinks = isMobile ? allLinks : allLinks.slice(visibleCount);

  return (
    <>
      {/* TOP NAVBAR */}
      <nav className="nv-navbar" ref={containerRef}>
        <div className="nv-left">
          <Link to="/" className="nv-logo">
            <i className="fas fa-ribbon"></i>
            <span>HəyatYolu</span>
          </Link>

          {!isMobile && visibleLinks.map(link => (
            <Link key={link.path} to={link.path} className="nv-link">
              {link.label}
            </Link>
          ))}

          {!isMobile && hiddenLinks.length > 0 && (
            <div className="nv-dropdown" ref={dropdownRef}>
              <button
                className="nv-dropdown-toggle"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                Daha çox ▾
              </button>
              {showDropdown && (
                <div className="nv-dropdown-menu nv-fade-in">
                  {hiddenLinks.map(link => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setShowDropdown(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="nv-right">
          {isMobile ? (
            <div className="nv-mobile-wrapper" ref={mobileMenuRef}>
              <button
                className="nv-mobile-btn"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                aria-label="Menu"
              >
                <span className={showMobileMenu ? "nv-menu-icon nv-open" : "nv-menu-icon"}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>

              {showMobileMenu && (
                <div className="nv-mobile-dropdown nv-fade-in">
                  <div className="nv-mobile-section">
                    {secondaryLinks.map(link => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setShowMobileMenu(false)}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>

                  {user && (
                    <>
                      <div className="nv-mobile-divider"></div>
                      <div className="nv-mobile-section">
                        <button onClick={handleLogout} className="nv-mobile-logout">
                          🚪 Çıxış
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="nv-btn-create" onClick={handleMemoryCreate}>
                <span className="nv-btn-icon">➕</span>
                Xatirə Yarat
              </button>
              {!user ? (
                <Link to="/giris" className="nv-btn-login">
                  Giriş
                </Link>
              ) : (
                <>
                  <Link to="/kabinetim" className="nv-btn-profile">
                    👤 <span className="nv-profile-text">Kabinetim</span>
                  </Link>
                  <button onClick={handleLogout} className="nv-btn-logout">
                    Çıxış
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </nav>

      {/* MOBILE BOTTOM NAVIGATION */}
      {isMobile && (
        <nav className="nv-bottom">
          {bottomNavItems.map((item) => (
            <button
              key={item.path}
              className={`nv-bottom-item ${isActiveRoute(item.path) ? 'nv-active' : ''} ${item.isCreate ? 'nv-bottom-create' : ''}`}
              onClick={() => handleBottomNavClick(item)}
            >
              {item.isCreate ? (
                <div className="nv-bottom-create-icon">
                  <i className={item.icon}></i>
                </div>
              ) : (
                <i className={item.icon}></i>
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      )}
    </>
  );
}

export default Navbar;